import {
   computeAttendanceAtTimeForRowCall,
   computeIfPerfectAttendanceForPeriod,
   computeAttendanceForPeriod,
} from "@/controller/attendanceCompute"
import { mergeQueryStates, QueryResult } from "@/utils/mergeQueryStates"
import { useQuery } from "@tanstack/react-query"
import { useCollection } from "../../hooks/useFirestore"
import { queryClient } from "../../main"
import { AttendanceStatus } from "../../types/attendanceStatus"
import { collectionName, User } from "./user"
import { dexieQueryRetryDelay } from "@/config/env"
import { appInitTime } from "@/utils/appInitTime"
import { useCurrentSchoolYear } from "../schoolYear/useSchoolYear"

export type AttendanceMetadata = {
   current_attendance_status: AttendanceStatus
   is_current_status_justified: boolean
}

export type YoungWithAttendance = User & AttendanceMetadata

export type AttendanceMetadataForRowCall = {
   current_attendance_status: AttendanceStatus
   is_current_attendance_expected: boolean
}

export type YoungWithAttendanceForRowCall = User & AttendanceMetadataForRowCall

export function invalidateQueriesForStatisticsAboutYoungsWithAttendance(refetch = false) {
   queryClient.invalidateQueries({
      queryKey: ["collection", "attendance_events", "statistics", "youngs"],
      refetchType: refetch ? "all" : "none",
   })
}

export function useYoungs() {
   return useCollection<User>({
      collectionName,
      suffixKey: ["youngs"],
      transform: (query) =>
         query
            .where("role")
            .equals("young")
            .filter((doc: User) => doc.is_active === true),
      onRefetch: () => {
         // TODO can we add a register function to register dependencies ? and then invalidate them automatically
         invalidateQueriesForStatisticsAboutYoungsWithAttendance()
      },
   })
}

export function useYoungsByGroup(group: string) {
   return useCollection<User>({
      collectionName,
      suffixKey: ["youngs", "by-group", group],
      transform: (query) =>
         query
            .where("role")
            .equals("young")
            .filter((doc: User) => doc.is_active === true && doc.group === group),
      enabled: !!group,
   })
}

export const useYoungsWithAttendanceForRowCallQueryKey = () => {
   return [
      "collection",
      "attendance_events",
      "statistics",
      "youngs",
      "with-attendance-at-time",
      "now=",
      "first-compute-time",
   ]
}

export function useYoungsWithAttendanceForRowCall({ now }: { now: Date }): {
   data: YoungWithAttendanceForRowCall[]
} & QueryResult {
   const { data: current_schoolyear_id } = useCurrentSchoolYear()

   const { data: users, ...restFirstQuery } = useYoungs()

   const { data: youngs, ...restSecondQuery } = useQuery({
      queryKey: useYoungsWithAttendanceForRowCallQueryKey(),
      meta: {
         first_compute_time: now,
      },
      queryFn: async () => {
         const timeSinceInit = Date.now() - appInitTime
         if (timeSinceInit < 4000) {
            await new Promise((resolve) => setTimeout(resolve, dexieQueryRetryDelay * 4))
         } else {
            await new Promise((resolve) => setTimeout(resolve, dexieQueryRetryDelay * 2 ))
         }

         const promises = (users || []).map(
            async (user: User): Promise<YoungWithAttendanceForRowCall> => {
               const { status, expected } = await computeAttendanceAtTimeForRowCall({
                  young_id: user.id,
                  time: now,
                  schoolyear_id: current_schoolyear_id!.id,
               })

               return {
                  ...user,
                  current_attendance_status: status,
                  is_current_attendance_expected: expected,
               } as YoungWithAttendanceForRowCall
            },
         )

         // We have only 30 youngs so we can do it in parallel
         const res = await Promise.all(promises)
         return res
      },
      enabled: !!users && !!current_schoolyear_id,
      refetchOnMount: true,
   })

   return {
      data: youngs || [],
      ...mergeQueryStates([restFirstQuery, restSecondQuery]),
   }
}

export function useYoungsWithPerfectAttendance({
   from,
   to,
   schoolyear_id,
}: {
   from?: Date
   to?: Date
   schoolyear_id?: string
}): {
   data: User[]
} & QueryResult {
   const { data: users, ...restFirstQuery } = useYoungs()

   const { data: youngs, ...restSecondQuery } = useQuery({
      enabled: !!users && !!schoolyear_id && !!from && !!to,

      queryKey: [
         "collection",
         "attendance_events",
         "statistics",
         "youngs",
         "with-perfect-attendance-at-period",
         from,
         to,
      ],

      queryFn: async () => {
         const timeSinceInit = Date.now() - appInitTime
         if (timeSinceInit < 4000) {
            await new Promise((resolve) => setTimeout(resolve, dexieQueryRetryDelay * 4))
         }

         const promises = (users || []).map(async (user: User): Promise<User | null> => {
            const hasPerfectAttendance = await computeIfPerfectAttendanceForPeriod({
               young_id: user.id,
               schoolyear_id: schoolyear_id!,
               from: from!,
               to: to!,
            })

            return hasPerfectAttendance ? user : null
         })

         // We have only 30 youngs so we can do it in parallel
         const res = await Promise.all(promises)
         return res
            .filter((young): young is User => young !== null)
            .sort((a, b) => a.firstName.localeCompare(b.firstName))
      },
      refetchOnMount: true,
   })

   return {
      data: youngs || [],
      ...mergeQueryStates([restFirstQuery, restSecondQuery]),
   }
}

export function useYoungsWithAttendanceForPeriod({
   from,
   to,
   schoolyear_id,
}: {
   from: Date
   to: Date
   schoolyear_id?: string
}): {
   data: YoungWithAttendance[]
} & QueryResult {
   const { data: users, ...restFirstQuery } = useYoungs()

   const { data: youngs, ...restSecondQuery } = useQuery({
      queryKey: [
         "collection",
         "attendance_events",
         "statistics",
         "youngs",
         "with-attendance-at-period",
         from,
         to,
      ],

      queryFn: async () => {
         const timeSinceInit = Date.now() - appInitTime
         if (timeSinceInit < 4000) {
            await new Promise((resolve) => setTimeout(resolve, dexieQueryRetryDelay * 4))
         }

         const promises = (users || []).map(async (user: User): Promise<YoungWithAttendance> => {
            const { status, justified } = await computeAttendanceForPeriod({
               young_id: user.id,
               schoolyear_id: schoolyear_id!,
               from,
               to,
            })

            return {
               ...user,
               current_attendance_status: status,
               is_current_status_justified: justified,
            }
         })

         const res = await Promise.all(promises)
         return res
      },
      enabled: !!users && !!schoolyear_id,
      refetchOnMount: true,
   })

   return {
      data: youngs || [],
      ...mergeQueryStates([restFirstQuery, restSecondQuery]),
   }
}
